<template>
    <div class="alert alert-danger" v-if="error_message">
        {{ error_message }}
    </div>
    <div v-if="options">
        <div class="row" v-if="!editMode">
            <div class="col-lg-10 col-xl-10">
                <div class="form-group">
                    <label for="title">{{ options.translations.form.TITLE }}</label>
                    <input type="text" id="title" class="form-control" minlength="6" maxlength="255"
                           required ref="form.title"
                           v-model="form.title" @change="validateField($event, 'title')"
                           v-bind:class="{'is-invalid': !validation.title.isValid && validation.title.isTouched}">
                    <div class="invalid-feedback d-block"
                         v-if="!validation.title.isValid && validation.title.isTouched">
                        {{ options.translations.errorMessages.TITLE }}
                    </div>
                </div>
            </div>
            <div class="col-lg col-xl">
                <div class="form-group">
                    <label for="number_of_products">{{ options.translations.form.NUMBER_OF_PRODUCTS }}</label>
                    <input id="number_of_products" type="number" step="1" required min="1" class="form-control"
                           v-model="form.number_of_products" ref="form.number_of_products"
                           @change="validateField($event, 'number_of_products')"
                           v-bind:class="{'is-invalid': !validation.number_of_products.isValid && validation.number_of_products.isTouched}">
                    <div class="invalid-feedback d-block"
                         v-if="!validation.number_of_products.isValid && validation.number_of_products.isTouched">
                        {{ options.translations.errorMessages.NUMBER_OF_PRODUCTS }}
                    </div>
                </div>
            </div>
        </div>
        <div class="row" v-if="!editMode">
            <div class="col-lg col-xl">
                <div class="form-group">
                    <label for="text">{{ options.translations.form.DESCRIPTION }}</label>
                    <textarea id="text" class="form-control" rows="5" v-model="form.text" required minlength="10"
                              maxlength="511"
                              @change="validateField($event, 'text')"
                              ref="form.text"
                              v-bind:class="{'is-invalid': !validation.text.isValid && validation.text.isTouched}"></textarea>
                    <div class="invalid-feedback d-block" v-if="!validation.text.isValid && validation.text.isTouched">
                        {{ options.translations.errorMessages.DESCRIPTION }}
                    </div>
                </div>
            </div>
        </div>
        <div class="row" v-if="!editMode">
            <div class="col-lg col-xl">
                <div class="form-group">
                    <label>{{ options.translations.form.EXTRA_FEATURES }}</label>
                    <div>
                        <div class="form-check form-check-inline">
                            <input id="certificate" class="form-check-input" type="checkbox" v-model="form.certificate">
                            <label for="certificate" class="form-check-label">
                                {{ options.translations.form.CERTIFICATE_PRESENT }}
                            </label>
                        </div>
                        <br class="display-mobile">
                        <div class="form-check form-check-inline">
                            <input id="receipt" class="form-check-input" type="checkbox" v-model="form.receipt">
                            <label for="receipt" class="form-check-label">
                                {{ options.translations.form.RECEIPT_PRESENT }}
                            </label>
                        </div>
                        <br class="display-mobile">
                        <div class="form-check form-check-inline">
                            <input id="dutyfree_stored" class="form-check-input" type="checkbox"
                                   v-model="form.dutyfree_stored">
                            <label for="dutyfree_stored" class="form-check-label">
                                {{ options.translations.form.DUTYFREE_STORED }}
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="row" v-if="!editMode">
            <div class="col-lg col-xl">
                <div class="form-group">
                    <label>{{ options.translations.form.PICTURES }}</label>
                    <image-upload @changeFiles="validateImages($event)" :pictures="form.pictures" :locale="locale"
                                  :file-types="allowedImageTypes"
                                  upload-action="upload.json"
                                  :max-size-per-image-kb="maxFileSize"></image-upload>
                    <div class="invalid-feedback d-block"
                         v-if="!validation.images.isValid && validation.images.isTouched">
                        {{ options.translations.errorMessages.PICTURES }}
                    </div>
                </div>
            </div>
        </div>
        <div class="row" v-if="!editMode">
            <div class="col-lg col-xl">
                <div class="form-group">
                    <p class="text-muted mb-0">{{ options.translations.form.RUNTIME_HINT }}</p>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-lg-6 col-xl-6">
                <div class="form-group">
                    <label for="min_price">
                        {{ options.translations.form.PRICE }}
                    </label>
                    <div class="input-group">
                        <div class="input-group-prepend">
                            <label for="min_price" class="input-group-text">
                                {{ options.translations.form.CHF }}
                                <span v-tooltip:top="options.translations.form.PRICE_TOOLTIP" class="ml-1">
                                    <i class="fa fa-info-circle"></i>
                                </span>
                            </label>
                        </div>
                        <input id="min_price" class="form-control" type="number" step="0.01" required min="1.00"
                               max="99999999"
                               v-model="form.min_price"
                               ref="form.min_price"
                               @change="minPriceChanged($event)"
                               v-bind:class="{'is-invalid': !validation.min_price.isValid && validation.min_price.isTouched}">
                        <div class="invalid-feedback d-block"
                             v-if="!validation.min_price.isValid && validation.min_price.isTouched">
                            {{ options.translations.errorMessages.MIN_PRICE }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="row" v-if="!editMode">
            <div class="col-lg col-xl">
                <div class="form-group">
                    <label>{{ options.translations.form.PICKUP_DELIVERY }}</label>
                    <select class="form-control" v-model="form.delivery_pickup" required
                            ref="form.delivery_pickup"
                            @change="validateField($event, 'delivery_pickup')"
                            v-bind:class="{'is-invalid': !validation.delivery_pickup.isValid && validation.delivery_pickup.isTouched}">
                        <option :value="key" :key="key" v-for="(label, key) in options.options.deliveryTypes">
                            {{ label }}
                        </option>
                    </select>
                </div>
            </div>
            <div class="col-lg col-xl">
                <div class="form-group">
                    <label>{{ options.translations.form.PAYMENT_TYPE }}</label>
                    <div>
                        <div class="form-check form-check-inline" :key="key"
                             v-for="(label, key) in options.options.paymentTypes">
                            <input :id="key" class="form-check-input" type="checkbox" v-model="form.payment_types"
                                   :value="key" @change="validatePaymentTypes()">
                            <label :for="key" class="form-check-label">{{ label }}</label>
                        </div>
                    </div>
                    <div class="invalid-feedback d-block"
                         v-if="!validation.payment_types.isValid && validation.payment_types.isTouched">
                        {{ options.translations.errorMessages.PAYMENT_TYPES }}
                    </div>
                </div>
            </div>
        </div>
        <div class="row" v-if="!editMode && !reconditionMode">
            <div class="col">
                <div class="alert alert-info" v-if="feeFormatted">
                    {{ options.translations.form.FEE_LABEL }}: <strong>{{ feeFormatted }}</strong>
                    <div class="mt-1" v-if="options.options.feeOnContact">
                        {{ options.translations.form.FEE_ON_CONTACT_HINT }}
                    </div>
                </div>
                <div class="alert alert-warning" v-else-if="feeError">
                    {{ options.translations.form.FEE_UNAVAILABLE }}
                </div>
            </div>
        </div>
        <div class="filter-buttons">
            <button class="btn btn-outline-secondary" @Click="back()">
                {{ options.translations.form.BACK }}
            </button>
            <button class="btn btn-secondary " type="submit" @Click="submit()" v-if="!this.editMode && !this.reconditionMode">
                {{ options.options.feeOnContact ? options.translations.form.PUBLISH_ON_CONTACT : options.translations.form.PUBLISH }}
            </button>
            <button class="btn btn-secondary " type="submit" @Click="submit()" v-if="this.editMode || this.reconditionMode">
                {{ options.translations.form.UPDATE }}
            </button>
        </div>
    </div>
    <loading-screen :is-loading="isLoading"></loading-screen>
</template>

<script>
import ImageUpload from "@/components/ImageUpload";
import LoadingScreen from "@/components/LoadingScreen";
import AdsApi from "../services/AdsApi";

export default {
    name: 'AdForm',
    components: {LoadingScreen, ImageUpload},
    data() {
        const formDefaults = {
            title: '',
            text: '',
            ad_type: 'FIXED_PRICE',
            certificate: false,
            receipt: false,
            dutyfree_stored: false,
            min_price: 0,
            max_price: null,
            delivery_pickup: null,
            number_of_products: 1,
            payment_types: '',
        };

        //Quickfix for migrating the single payment method to two payment methods
        let formValue = Object.assign(formDefaults, this.offer);
        formValue.payment_types = this.offer.payment_type || [];
        delete formValue.payment_type;

        // Set price data to defaults, when its not the edit mode
        if (!this.editMode) {
            formValue.min_price = 0;
            formValue.max_price = null;
        }

        formValue.receipt = this.offer.reciept || false;

        return {
            // Loading spinner control
            isLoading: false,

            // Translations for error messages and labels
            options: null,

            // Error message display
            error_message: null,

            // override the form defaults with passed data
            form: formValue,

            pictures: this.offer.pictures ?? '',
            images: [],

            // Advertising fee preview (shown before publishing a new ad)
            feeFormatted: null,
            feeError: false,

            // Validation state
            validation: {
                title: {isValid: false, isTouched: false},
                number_of_products: {isValid: true, isTouched: false},
                text: {isValid: false, isTouched: false},
                images: {isValid: false, isTouched: false},
                min_price: {isValid: false, isTouched: false},
                max_price: {isValid: true, isTouched: false},
                payment_types: {isValid: false, isTouched: false},
                delivery_pickup: {isValid: false, isTouched: false},
            }
        };
    },
    props: ['locale', 'offer', 'allowedImageTypes', 'maxFileSize', 'saveHandler', 'editMode', 'reconditionMode'],
    created() {
        this.initOptions();

        // The fee only applies when publishing a new ad.
        if (!this.editMode && !this.reconditionMode) {
            this.loadFee();
        }
    },
    watch: {
        'form.number_of_products'() {
            if (!this.editMode && !this.reconditionMode) {
                this.loadFee();
            }
        }
    },
    methods: {
        initOptions() {
            // Fetch translated labels and error messages
            AdsApi.getCreateOptions(this.locale).then(body => this.options = body);
        },
        loadFee() {
            const numberOfItems = parseInt(this.form.number_of_products, 10) || 1;

            AdsApi.getAdFee(this.locale, this.form.product_id, numberOfItems).then(body => {
                this.feeFormatted = body.feeFormatted;
                this.feeError = body.fee === null;
            }).catch(() => {
                this.feeFormatted = null;
                this.feeError = true;
            });
        },
        updateValidity() {
            const inputValidationFields = [
                "title",
                "number_of_products",
                "text",
                "delivery_pickup",
            ];

            for (let i = 0; i < inputValidationFields.length; i++) {
                let field = inputValidationFields[i];

                let valid = false;

                if (this.editMode) {
                    // These fields cannot be changed in editmode, so they must be set to valid.
                    valid = true;
                } else {
                    let input = this.$refs["form." + field];
                    valid = input.checkValidity();
                }

                this.validation[field] = {
                    isValid: valid,
                    isTouched: true
                };
            }


            // Validate prices
            this.validateMinPrice();
            this.validateMaxPrice();

            // These fields cannot be changed in editmode, so they must be set to valid.
            if (this.editMode) {
                this.validation['payment_types'] = {
                    isValid: true,
                    isTouched: true
                };
                this.validation['images'] = {
                    isValid: true,
                    isTouched: true
                };
            } else {
                // Validate the PaymentTypes, when its not the editmode
                this.validatePaymentTypes();
            }

        },
        submit() {
            this.updateValidity();

            this.error_message = null;

            if (this.isFormValid() && !this.isLoading) {
                this.isLoading = true;

                //Use saveHandler to handle the save request so this component
                //can be used for different aspects, after trying to save disable the loading spinner again
                this.saveHandler(this.buildRequestBody())
                    .then((response_body) => {
                        if (response_body.data) {
                            window.location.href = '/ads/view/' + response_body.data.id;
                        } else {
                            // Set error message if there is any
                            this.error_message = response_body.message;
                        }
                    })
                    .finally(() => this.isLoading = false);
            }
        },
        buildRequestBody() {
            this.form.payment_type = this.form.payment_types;

            return Object.assign({}, this.form, {images: this.images});
        },
        validateField(e, fieldName) {
            // Special price validation first
            if (fieldName === 'min_price') {
                this.validateMinPrice(e);

                this.setValidation(fieldName, this.$refs['form.min_price'].checkValidity());
            } else if (fieldName === 'max_price') {
                this.validateMaxPrice(e);
            } else {
                // Standard validation based on html inputs
                this.setValidation(fieldName, e.target.checkValidity());
            }
        },
        setValidation(field, isValid, isTouched = true) {
            this.validation[field] = {isValid, isTouched};
        },
        validateImages(files) {
            this.images = files;
            if (this.images.length > 0) {
                this.setValidation('images', true);
            } else {
                this.setValidation('images', false);
            }
        },
        validateMaxPrice(e) {
            let value = e ? e.target.value : this.form.max_price;

            if (value === null || value === '') {
                this.setValidation('max_price', true);
                return;
            }

            value = this.parsePrice(value.toString());
            this.form.max_price = value;

            const minPrice = parseFloat(this.form.min_price);
            const maxPrice = parseFloat(this.form.max_price);
            const isValid = isNaN(maxPrice) || maxPrice >= minPrice;

            this.setValidation('max_price', isValid);
        },
        validateMinPrice(e) {
            let value = e ? e.target.value : this.form.min_price;

            if (value === null || value === '') {
                this.setValidation('min_price', false);
                return;
            }

            value = this.parsePrice(value.toString());
            this.form.min_price = value;

            // Min and max prices are always the same, this is an invariant
            this.form.max_price = this.form.min_price;

            const minPrice = parseFloat(this.form.min_price);
            this.setValidation('min_price', !isNaN(minPrice) && minPrice >= 1.0);
        },
        minPriceChanged(e) {
            this.validateMinPrice(e);

            if(this.editMode) {
                this.validateMaxPrice(e)
            }
        },
        validatePaymentTypes() {
            if (Object.keys(this.form.payment_types).length > 0) {
                this.setValidation('payment_types', true);
            } else {
                this.setValidation('payment_types', false);
            }
        },
        isFormValid() {
            let isValid = true;
            Object.keys(this.validation).forEach(field => {
                this.validation[field]['isTouched'] = true;

                if (!this.validation[field]['isValid']) {
                    isValid = false;
                }
            });
            return isValid;
        },
        parsePrice(price) {
            price = price ? parseFloat(price).toFixed(1) + '0' : null;
            price.replace(".", ",");
            return price;
        },
        back() {
            if (this.editMode) {
                // If it is the edit mode, redirect back to the offer
                window.location.href = '/' + this.locale + '/ads/view/' + this.offer.id;
            } else {
                if (confirm(this.options.translations.form.BACK_MESSAGE).valueOf()) {

                    window.location.href = '/' + this.locale + '/ads/init';
                }
            }
        }
    }
};
</script>

<style scoped>
</style>
